/* GrowthOS — Account detail "Member Accounts" tab.
   UI ported from the claude.ai/design "growthos" AccountRecord.MemberAccounts: a
   searchable table (Member Account Name / Industry / Company LinkedIn / Website /
   Phone / Annual Revenue). Wired to the real child-account list
   (apiServices.getChildAccountByParentAccountId) — same fields, same API the legacy
   (unstyled) `memberAccount.jsx` grid used. "Add Member Account" navigates to the
   real add-account form (blank — like the design's button, which has no handler
   either; the form has no parent-account-prefill support to wire into). */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiServices from '../../services/service';
import { Ic } from "../../shared/component/GosIcon";


const AVA_COLORS = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480', '#54738f'];
const initials = (name) =>
  String(name || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase() || '?';
const avaColor = (name) => AVA_COLORS[String(name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % AVA_COLORS.length];

const fmtRevenue = (v) => {
  if (v == null || v === '') return '—';
  const n = Number(String(v).replace(/[^0-9.-]/g, ''));
  if (!isFinite(n) || n === 0) return '—';
  return '$' + (Math.abs(n) >= 1000 ? (n / 1000).toFixed(n % 1000 === 0 ? 0 : 1) + 'K' : String(n));
};

const AccountMemberAccountsTab = ({ account }) => {
  const navigate = useNavigate();
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!account?.id) return;
    setLoading(true);
    apiServices
      .getChildAccountByParentAccountId({ id: account.id })
      .then((response) => {
        if (response.status === 200) setAll(response.data?.account || []);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching member accounts:', error);
        setLoading(false);
      });
  }, [account?.id]);

  const rows = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return all;
    return all.filter((m) => `${m.name || ''} ${m.industry || ''}`.toLowerCase().includes(ql));
  }, [all, q]);

  return (
    <div className="gos-oppr-card">
      <div className="gos-oppr-head">
        <div className="gos-oppr-title">
          <Ic name="accounts" size={16} />
          Member Accounts
          <span className="gos-oppr-count">{all.length}</span>
        </div>
        <button type="button" className="gos-oppr-btn soft" onClick={() => navigate('/accounts/addAccount')}>
          <Ic name="plus" size={13} />
          Add Member Account
        </button>
      </div>

      <div className="gos-set-search-row">
        <div className="gos-set-search">
          <Ic name="search" size={16} />
          <input placeholder="Search in member accounts…" value={q} onChange={(e) => setQ(e.target.value)} />
          {q ? (
            <button type="button" className="gos-import-tpl-btn" style={{ border: 0, padding: 4 }} onClick={() => setQ('')} aria-label="Clear">
              <Ic name="x" size={13} />
            </button>
          ) : null}
        </div>
      </div>

      {loading ? (
        <div className="gos-oppr-empty">
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        </div>
      ) : rows.length === 0 ? (
        <div className="gos-oppr-empty">
          <div className="gos-oppr-empty-ic">
            <Ic name="accounts" size={21} />
          </div>
          <div className="gos-oppr-empty-t">{all.length === 0 ? 'No member accounts' : 'No matches'}</div>
          <div className="gos-oppr-empty-s">
            {all.length === 0 ? 'Child accounts linked to this account will appear here.' : `Nothing matches “${q}”.`}
          </div>
        </div>
      ) : (
        <div className="gos-set-table-wrap">
          <table className="gos-set-table">
            <thead>
              <tr>
                <th>Member Account Name</th>
                <th>Industry</th>
                <th>Company LinkedIn</th>
                <th>Website</th>
                <th>Phone</th>
                <th>Annual Revenue</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m, i) => (
                <tr key={m.id ?? i}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span className="gos-bulk-ava" style={{ width: 30, height: 30, fontSize: 11, background: avaColor(m.name) }}>
                        {initials(m.name)}
                      </span>
                      <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{m.name || '—'}</span>
                    </div>
                  </td>
                  <td>{m.industry || '—'}</td>
                  <td>
                    {m.companyLinkedIn ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--gos-accent-ink)', fontWeight: 550, whiteSpace: 'nowrap' }}>
                        <Ic name="link" size={13} /> {m.companyLinkedIn}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{m.website || '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{m.phone || '—'}</td>
                  <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtRevenue(m.annualRevenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AccountMemberAccountsTab;
